// ResponsiveTable.js
import { useEffect, useRef, useState } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { createPortal } from "react-dom";

export default function ResponsiveTable({
  columns = [],
  data = [],
  showActions = false,
  showActionButton = true,
  inlineActions = false,
  onActionClick,
  actionButtons = [], // dynamic action array with {label, value, icon, className}
}) {
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRefs = useRef([]);

  const [sortBy, setSortBy] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  const filteredData = data.filter((row) =>
    columns.some((col) =>
      String(row[col.accessor] ?? "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(key);
      setSortAsc(true);
    }
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortBy) return 0;
    const valA = a[sortBy];
    const valB = b[sortBy];
    if (typeof valA === "number" && typeof valB === "number") {
      return sortAsc ? valA - valB : valB - valA;
    }
    return sortAsc
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const currentData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-action-menu")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openActionDropdown = (idx) => {
    const buttonRect = buttonRefs.current[idx].getBoundingClientRect();
    const dropdownHeight = 160; // estimated height of the dropdown
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    const openAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

    setDropdownStyle({
      position: "fixed",
      top: openAbove ? buttonRect.top - dropdownHeight - 4 : buttonRect.bottom + 4,
      left: buttonRect.right - 144,
      zIndex: 9999,
    });

    setOpenDropdown(idx);
  };

  return (
    <div className="p-2">
      <div className="flex flex-col md:flex-row md:justify-between items-center mb-4 gap-2">
        <input
          type="text"
          placeholder="Search..."
          className="border px-3 py-1 rounded w-full md:w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="border px-3 py-1 rounded"
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              {size} rows
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border-gray-300 border rounded shadow text-sm table-auto">
          <thead className="bg-blue-50 text-blue-800 font-medium sticky top-0">
            <tr>
              {columns.map((col, colIndex) => (
                <th
                  key={col.accessor}
                  onClick={() => handleSort(col.accessor)}
                  className={`text-[14px] border border-gray-300 px-2 py-2 border-b cursor-pointer ${col.align === "right"
                    ? "text-right"
                    : col.align === "center"
                      ? "text-center"
                      : "text-left"
                    } ${colIndex === 0 ? "sticky left-0 bg-white z-10" : ""}`}
                >
                  {col.header}
                </th>
              ))}
              {showActions && <th className="px-2 py-2 border-b text-left">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {currentData.map((row, idx) => (
              <tr key={idx} className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100`}>
                {columns.map((col, colIndex) => (
                  <td
                    key={col.accessor}
                    className={`text-[13px] px-2 border border-gray-300 py-2 border-b ${col.align === "right"
                        ? "text-right"
                        : col.align === "center"
                          ? "text-center"
                          : "text-left"
                      } ${colIndex === 0 ? "sticky left-0 bg-white z-10" : ""}`}
                  >
                    {col.render ? col.render(row[col.accessor], row, idx) : row[col.accessor]}
                  </td>
                ))}
                {showActions && (
                  <td className="px-2 py-2 relative flex justify-center items-center">
                    {inlineActions ? (
                      <div className="flex gap-2">
                        {actionButtons.map((action) => (
                          <button
                            key={action.value}
                            onClick={() => {
                              onActionClick(action.value, row);
                              setOpenDropdown(null);
                            }}
                            title={action.label}
                          >
                            {action.icon || action.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      showActionButton && (
                        <>
                          <button ref={(el) => (buttonRefs.current[idx] = el)} onClick={() => openActionDropdown(idx)}>
                            <EllipsisVerticalIcon className="w-5 h-5" />
                          </button>
                          {openDropdown === idx &&
                            createPortal(
                              <div
                                className="dropdown-action-menu fixed w-36 bg-white shadow rounded z-50 text-sm"
                                style={dropdownStyle}
                              >
                                {actionButtons.map((action, i) =>
                                  action.divider ? (
                                    <hr key={`divider-${i}`} className="my-1 border-gray-200" />
                                  ) : (
                                    <button
                                      key={i}
                                      onClick={() => {
                                        onActionClick(action.value, row);
                                        setOpenDropdown(null);
                                      }}
                                      className={`flex text-[13px] w-full px-4 py-1.5 hover:bg-gray-100 ${action.className || ''}`}
                                    >
                                      {action.icon && <span className="mr-2">{action.icon}</span>}
                                      {action.label}
                                    </button>
                                  )
                                )}
                              </div>,
                              document.body
                            )}
                        </>
                      )
                    )}
                  </td>
                )}
              </tr>
            ))}
            {currentData.length === 0 && (
              <tr>
                <td colSpan={columns.length + (showActions ? 1 : 0)} className="text-center border border-gray-300 px-2 py-4 text-gray-400">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="text-sm text-gray-500">Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)}  of {filteredData.length} entries</div>
        <div className="flex gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}